/**
 * PLAYER module — touch input scheme for coarse-pointer devices.
 * Left ~45% of the screen anchors a virtual joystick (move); any other
 * touch drags the camera (look). A short, still touch on the look side is
 * a tap → interact. touchend calls preventDefault so the browser never
 * synthesizes a click (which would double-fire interact via the canvas
 * click path). Owned by PLAYER; see CONTRACTS.md.
 */
import {
  JOYSTICK_DEAD_ZONE,
  JOYSTICK_RADIUS_PX,
  JOYSTICK_ZONE_FRACTION,
  TAP_MAX_DURATION_MS,
  TAP_MAX_TRAVEL_PX,
  TOUCH_LOOK_SENSITIVITY
} from './constants'

export interface MoveVector {
  readonly forward: number
  readonly strafe: number
}

export interface TouchControlsHandle {
  /** Current joystick deflection, each axis in [-1, 1]. */
  moveVector(): MoveVector
  dispose(): void
}

interface LookTouch {
  readonly id: number
  readonly startX: number
  readonly startY: number
  readonly startedAt: number
  lastX: number
  lastY: number
  travelled: number
}

interface MoveTouch {
  readonly id: number
  readonly originX: number
  readonly originY: number
  forward: number
  strafe: number
}

const JOYSTICK_RING_STYLE =
  'position:fixed;width:112px;height:112px;margin:-56px 0 0 -56px;' +
  'border:2px solid rgba(216,184,96,0.4);border-radius:50%;' +
  'pointer-events:none;z-index:40;display:none'
const JOYSTICK_NUB_STYLE =
  'position:fixed;width:44px;height:44px;margin:-22px 0 0 -22px;' +
  'background:rgba(216,184,96,0.35);border:1px solid rgba(216,184,96,0.7);' +
  'border-radius:50%;pointer-events:none;z-index:41;display:none'

export const createTouchControls = (
  getSurface: () => Element | null,
  onLookDelta: (dx: number, dy: number) => void,
  onTap: () => void
): TouchControlsHandle => {
  let moveTouch: MoveTouch | null = null
  let lookTouch: LookTouch | null = null

  const ring = document.createElement('div')
  ring.style.cssText = JOYSTICK_RING_STYLE
  const nub = document.createElement('div')
  nub.style.cssText = JOYSTICK_NUB_STYLE
  document.body.append(ring, nub)

  const showJoystick = (x: number, y: number): void => {
    ring.style.display = 'block'
    nub.style.display = 'block'
    ring.style.left = `${x}px`
    ring.style.top = `${y}px`
    moveNub(x, y)
  }
  const moveNub = (x: number, y: number): void => {
    nub.style.left = `${x}px`
    nub.style.top = `${y}px`
  }
  const hideJoystick = (): void => {
    ring.style.display = 'none'
    nub.style.display = 'none'
  }

  const updateMove = (touch: MoveTouch, clientX: number, clientY: number): void => {
    const rawX = clientX - touch.originX
    const rawY = clientY - touch.originY
    const length = Math.hypot(rawX, rawY)
    const clamp = length > JOYSTICK_RADIUS_PX ? JOYSTICK_RADIUS_PX / length : 1
    const dx = (rawX * clamp) / JOYSTICK_RADIUS_PX
    const dy = (rawY * clamp) / JOYSTICK_RADIUS_PX
    const deflection = Math.hypot(dx, dy)
    const live = deflection >= JOYSTICK_DEAD_ZONE
    touch.forward = live ? -dy : 0
    touch.strafe = live ? dx : 0
    moveNub(touch.originX + rawX * clamp, touch.originY + rawY * clamp)
  }

  const onTouchStart = (event: TouchEvent): void => {
    if (event.target !== getSurface()) return
    event.preventDefault()
    for (const t of Array.from(event.changedTouches)) {
      const inMoveZone = t.clientX < window.innerWidth * JOYSTICK_ZONE_FRACTION
      if (inMoveZone && !moveTouch) {
        moveTouch = {
          id: t.identifier,
          originX: t.clientX,
          originY: t.clientY,
          forward: 0,
          strafe: 0
        }
        showJoystick(t.clientX, t.clientY)
      } else if (!lookTouch) {
        lookTouch = {
          id: t.identifier,
          startX: t.clientX,
          startY: t.clientY,
          startedAt: performance.now(),
          lastX: t.clientX,
          lastY: t.clientY,
          travelled: 0
        }
      }
    }
  }

  const onTouchMove = (event: TouchEvent): void => {
    for (const t of Array.from(event.changedTouches)) {
      if (moveTouch && t.identifier === moveTouch.id) {
        event.preventDefault()
        updateMove(moveTouch, t.clientX, t.clientY)
      } else if (lookTouch && t.identifier === lookTouch.id) {
        event.preventDefault()
        const dx = t.clientX - lookTouch.lastX
        const dy = t.clientY - lookTouch.lastY
        lookTouch.travelled += Math.hypot(dx, dy)
        lookTouch.lastX = t.clientX
        lookTouch.lastY = t.clientY
        onLookDelta(dx * TOUCH_LOOK_SENSITIVITY, dy * TOUCH_LOOK_SENSITIVITY)
      }
    }
  }

  const onTouchEnd = (event: TouchEvent): void => {
    for (const t of Array.from(event.changedTouches)) {
      if (moveTouch && t.identifier === moveTouch.id) {
        event.preventDefault()
        moveTouch = null
        hideJoystick()
      } else if (lookTouch && t.identifier === lookTouch.id) {
        event.preventDefault()
        const wasTap =
          lookTouch.travelled <= TAP_MAX_TRAVEL_PX &&
          performance.now() - lookTouch.startedAt <= TAP_MAX_DURATION_MS
        lookTouch = null
        if (wasTap) onTap()
      }
    }
  }

  window.addEventListener('touchstart', onTouchStart, { passive: false })
  window.addEventListener('touchmove', onTouchMove, { passive: false })
  window.addEventListener('touchend', onTouchEnd, { passive: false })
  window.addEventListener('touchcancel', onTouchEnd, { passive: false })

  return {
    moveVector: () =>
      moveTouch
        ? { forward: moveTouch.forward, strafe: moveTouch.strafe }
        : { forward: 0, strafe: 0 },
    dispose: () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchEnd)
      ring.remove()
      nub.remove()
    }
  }
}
